import React, {useCallback, useEffect, useState} from 'react';
import {IFarmer, IFarmInputProgramInput, IProduce} from 'constants/interfaces';
import {Controller, useForm} from 'react-hook-form';
import {
  Button,
  Chip,
  CircularProgress,
  Collapse,
  FormControl,
  FormHelperText,
  Grid,
  TextField,
  Typography,
  Box,
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import {Skeleton} from '@material-ui/lab';
import {
  apiGetFarmer,
  apiGetUser,
  apiSearchFarmersAsSupplier,
} from 'constants/Api';
import {FarmerList} from 'screens/Dashboard/Quotes/QuoteCreate/QuoteCreate';
// ^ We keep FarmerList as-is for selecting/deselecting after searching.

import './ProgramForm.scss';

import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faSparkles, faSearch} from '@fortawesome/pro-regular-svg-icons';

interface ProduceObject {
  id: number;
  name: string;
}
interface PolygonInfo {
  hectares: number;
  produce: ProduceObject;
  fieldName: string;
  variety?: string;
  plantedOn?: string;
  irrigation?: string;
  expectedYield?: number;
}
interface PolygonSoilData {
  dateCollected?: string;
  soilType?: string | null;
  pH?: number;
  P?: number;
  K?: number;
  Ca?: number;
  Mg?: number;
  Na?: number;
  cations?: number;
  anions?: number;
  exchangeableAcidity?: number;
}
interface FarmPolygon {
  id: string;
  info: PolygonInfo;
  soilData?: PolygonSoilData;
  createdAt?: string;
  coordinates?: any[];
}

type Submission = IFarmInputProgramInput & {
  fieldPolygon?: FarmPolygon;
  produceId?: string | number;
  soilData?: PolygonSoilData;
};

/**
 * A brand-new search bar component that replaces FarmerSearch.
 * It receives the same props (onSearchTermChange, isLoading)
 * but has local styling and a Font Awesome search icon.
 * @param {Object} props
 * @return {JSX.Element}
 */
const NewFarmerSearchBar = (props: {
  onSearchTermChange: (term: string) => void;
  isLoading: boolean;
}) => {
  const [localValue, setLocalValue] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalValue(e.target.value);
    props.onSearchTermChange(e.target.value);
  };

  return (
    <div className="new-farmer-search-bar">
      <FontAwesomeIcon icon={faSearch} className="search-icon" />
      <input
        className="search-input"
        type="text"
        placeholder="Search farmers"
        value={localValue}
        onChange={handleChange}
      />
      {props.isLoading && (
        <CircularProgress size={20} className="search-loader" />
      )}
    </div>
  );
};

export function ProgramForm(props: {
  program?: IFarmInputProgramInput;
  isLoadingProduce: boolean;
  allProduce: IProduce[];
  isLoadingProgramTypes: boolean;
  programTypes: string[];
  isLoading: boolean;
  submitButtonTitle: string;
  onSubmit: (
    program: IFarmInputProgramInput & {fieldPolygon?: FarmPolygon},
  ) => any;
  cancelButtonTitle?: string;
  onCancel?: () => any;
  smallWidth?: boolean;
  readonly?: boolean;
  handleAIClick: () => void; // AI click handler
}) {
  const {isLoading} = props;

  // Example of local farmPolygons data
  /* const [farmPolygons] = useState<FarmPolygon[]>(() => [
    {
      id: 'field1',
      info: {
        fieldName: 'Field #1',
        produce: {id: 1, name: 'Maize'},
        hectares: 10,
        variety: 'SC701',
        plantedOn: '2023-01-15',
        irrigation: 'Drip',
        plantPopulation: 70000,
        expectedYield: 15.5,
      },
      soilData: {
        dateCollected: '2023-02-01',
        soilType: 'Loamy',
        pH: 6.1,
        p: 40,
        k: 25,
        ca: 12.0,
        mg: 5.8,
        na: 2.5,
        cations: 3.1,
        anions: 1.2,
        zn: 0.55,
        mn: 0.75,
        cu: 0.25,
        exchangeableAcidity: 0.5,
      },
      createdAt: '2023-01-01T10:00:00Z',
      coordinates: [],
    },
    {
      id: 'field2',
      info: {
        fieldName: 'Field #2',
        produce: {id: 1, name: 'Potato'},
        hectares: 5,
        variety: 'Spunta',
        plantedOn: '2023-03-01',
        irrigation: 'Center Pivot',
        plantPopulation: 50000,
        expectedYield: 8.2,
      },
      soilData: {
        dateCollected: '2023-02-10',
        soilType: 'Sandy',
        pH: 5.5,
        p: 30,
        k: 20,
        ca: 10.5,
        mg: 4.0,
        na: 2.2,
        cations: 2.8,
        anions: 1.0,
        zn: 0.35,
        mn: 0.5,
        cu: 0.2,
        exchangeableAcidity: 0.3,
      },
      createdAt: '2023-02-15T08:30:00Z',
      coordinates: [],
    },
    {
      id: 'field3',
      info: {
        fieldName: 'Field #3',
        produce: {id: 1, name: 'Celeste XL Red 1L'},
        hectares: 12,
        variety: 'CEL-Red',
        plantedOn: '2022-09-10',
        irrigation: 'Sprinkler',
        plantPopulation: 60000,
        expectedYield: 11.0,
      },
      soilData: {
        dateCollected: '2022-11-01',
        soilType: 'Clay',
        pH: 5.0,
        p: 22,
        k: 15,
        ca: 8,
        mg: 3.1,
        na: 1.9,
        cations: 1.6,
        anions: 0.8,
        zn: 0.2,
        mn: 0.3,
        cu: 0.1,
        exchangeableAcidity: 0.2,
      },
      createdAt: '2022-11-05T14:20:00Z',
      coordinates: [],
    },
  ]);*/

  const [farmerSearchTerm, setFarmerSearchTerm] = useState('');
  const [isLoadingFarmers, setIsLoadingFarmers] = useState(false);
  const [farmers, setFarmers] = useState<IFarmer[]>([]);
  const [selectedFarmer, setSelectedFarmer] = useState<IFarmer>();
  const [showSoilData, setShowSoilData] = useState(false);

  // Soil types
  const SOIL_TYPES = ['Clay', 'Loamy', 'Sandy', 'Peat', 'Chalky'];

  // React Hook Form
  const {handleSubmit, control, setValue, reset} = useForm<Submission>({
    defaultValues: {
      id: props.program?.id,
      active:
        props.program?.active === undefined ? true : props.program?.active,
      farmerId: props.program?.farmerId,
      farmerEmail: props.program?.farmerEmail,
      produceId: props.program?.produceId,
      type: props.program?.type,
      status: props.program?.status,
      hectares: props.program?.hectares,
      fieldId: props.program?.fieldId,
      fieldPolygon: (props.program as any)?.fieldPolygon || undefined,
      soilData: (props.program as any)?.fieldPolygon?.soilData || {
        soilType: null,
        pH: undefined,
        P: undefined,
        K: undefined,
        Ca: undefined,
        Mg: undefined,
        Na: undefined,
        cations: undefined,
        anions: undefined,
        exchangeableAcidity: undefined,
      },
      // Preserve core program properties
      lines: props.program?.lines,
    },
  });

  // Reset form when program prop changes (e.g., when editing different program)
  useEffect(() => {
    if (props.program) {
      const existingSoilData = (props.program as any)?.fieldPolygon?.soilData;
      const hasSoilData =
        existingSoilData && Object.keys(existingSoilData).length > 0;

      reset({
        id: props.program.id,
        active:
          props.program.active === undefined ? true : props.program.active,
        farmerId: props.program.farmerId,
        farmerEmail: props.program.farmerEmail,
        produceId: props.program.produceId,
        type: props.program.type,
        status: props.program.status,
        hectares: props.program.hectares,
        fieldId: props.program.fieldId,
        fieldPolygon: (props.program as any)?.fieldPolygon || undefined,
        soilData: existingSoilData || {
          soilType: null,
          pH: undefined,
          P: undefined,
          K: undefined,
          Ca: undefined,
          Mg: undefined,
          Na: undefined,
          cations: undefined,
          anions: undefined,
          exchangeableAcidity: undefined,
        },
        lines: props.program.lines,
      });
      // Show soil data section if soil data exists
      setShowSoilData(hasSoilData);
    }
  }, [props.program, reset]);

  // If there's a known farmer, load data
  useEffect(() => {
    console.log('Farmer loading effect triggered:', {
      program: props.program,
      hasFarmer: !!props.program?.farmer,
      farmerEmail: props.program?.farmerEmail,
      farmerObjectEmail: props.program?.farmer?.email,
      farmerUserId: props.program?.farmer?.userId,
      farmerId: props.program?.farmerId,
    });

    // If program already has farmer object with fields, use it directly
    if (props.program?.farmer?.farm?.fields) {
      console.log(
        'Using farmer from program with fields:',
        props.program.farmer,
      );
      setSelectedFarmer(props.program.farmer);
      return;
    }

    // Try to get farmer email or userId
    const email = props.program?.farmerEmail || props.program?.farmer?.email;
    const userId = props.program?.farmer?.userId;

    // If we have email, fetch by email
    if (email) {
      console.log('Fetching farmer data by email:', email);
      setIsLoadingFarmers(true);
      apiGetFarmer(email)
        .then((farmer) => {
          console.log('Farmer loaded with fields (by email):', farmer);
          setSelectedFarmer(farmer);
        })
        .catch((err) => {
          alert('Error loading farmer. Please try again later');
          console.error(err);
        })
        .finally(() => setIsLoadingFarmers(false));
      return;
    }

    // If we have userId, fetch through apiGetUser
    if (userId) {
      console.log('Fetching user/farmer data by userId:', userId);
      setIsLoadingFarmers(true);
      apiGetUser(userId)
        .then((user) => {
          console.log('User loaded:', user);
          if (user?.farmer) {
            console.log('Farmer loaded with fields (by userId):', user.farmer);
            setSelectedFarmer(user.farmer);
          } else {
            console.warn('User has no farmer object');
            setSelectedFarmer(undefined);
          }
        })
        .catch((err) => {
          alert('Error loading farmer. Please try again later');
          console.error(err);
        })
        .finally(() => setIsLoadingFarmers(false));
      return;
    }

    // No way to fetch farmer
    console.warn(
      'No farmer email or userId found in program, cannot load fields',
    );
    setSelectedFarmer(undefined);
  }, [props.program?.farmer, props.program?.farmerEmail]);

  // Set fieldPolygon when farmer is loaded and we have a fieldId
  // Only set if fieldPolygon is not already set
  useEffect(() => {
    console.log('Field effect running:', {
      hasFields: !!selectedFarmer?.farm?.fields,
      fieldsCount: selectedFarmer?.farm?.fields?.length || 0,
      fieldId: props.program?.fieldId,
      existingFieldPolygon: !!(props.program as any)?.fieldPolygon,
      fields: selectedFarmer?.farm?.fields,
    });

    // If fieldPolygon already exists in program, use it (don't override)
    if ((props.program as any)?.fieldPolygon) {
      console.log('Using existing fieldPolygon from program');
      return;
    }

    // Otherwise, try to find field by fieldId when farmer is loaded
    if (selectedFarmer?.farm?.fields && props.program?.fieldId) {
      const field = selectedFarmer.farm.fields.find(
        (f) => f.id === props.program?.fieldId,
      );
      console.log('Found field:', field);
      if (field) {
        setValue('fieldPolygon', field as any);
      } else {
        console.warn('Field not found with id:', props.program?.fieldId);
      }
    }
  }, [selectedFarmer, props.program?.fieldId, props.program, setValue]);

  // Final form submission
  const onSubmit = (submission: Submission) => {
    // Merge soil data into fieldPolygon if it exists
    let fieldPolygonWithSoil: any = submission.fieldPolygon;
    if (submission.soilData && Object.keys(submission.soilData).length > 0) {
      fieldPolygonWithSoil = {
        ...(submission.fieldPolygon || {}),
        soilData: {
          ...submission.soilData,
          dateCollected:
            submission.soilData.dateCollected || new Date().toISOString(),
        },
      };
    }

    // Preserve original program data and merge with form changes
    const updatedProgram: any = {
      ...props.program, // Keep all original program data
      ...submission, // Override with form changes
      fieldPolygon: fieldPolygonWithSoil,
      // Set fieldId from fieldPolygon if it exists
      fieldId:
        fieldPolygonWithSoil?.id ||
        submission.fieldId ||
        props.program?.fieldId,
      hectares: parseFloat(submission.hectares as any),
      // Ensure farmer data is preserved
      farmer: selectedFarmer || props.program?.farmer,
      farmerId: submission.farmerId || props.program?.farmerId,
      farmerEmail: submission.farmerEmail || props.program?.farmerEmail,
    };

    props.onSubmit(updatedProgram as any);
  };

  // Debounced load farmers
  const loadFarmers = useCallback(() => {
    setIsLoadingFarmers(true);
    apiSearchFarmersAsSupplier(farmerSearchTerm)
      .then(setFarmers)
      .catch((error) => {
        alert('Error loading farmers. Please try again later');
        console.error(error);
      })
      .finally(() => setIsLoadingFarmers(false));
  }, [farmerSearchTerm]);

  useEffect(() => {
    if (farmerSearchTerm.length) {
      setIsLoadingFarmers(true);
      const timeout = setTimeout(loadFarmers, 1000);
      return () => clearTimeout(timeout);
    }
  }, [farmerSearchTerm, loadFarmers]);

  // Farmer selection
  const handleFarmerSelect = (farmer: IFarmer) => {
    setSelectedFarmer(farmer);
    setValue('farmerId', farmer.id.toString());
    setValue('farmerEmail', farmer.email);
  };
  const handleFarmerDeselect = () => {
    setSelectedFarmer(undefined);
  };

  // For the crop Autocomplete
  const findProduce = (id: any) => {
    return props.allProduce?.find((produce) => produce.id === id);
  };

  return (
    <div className="program-form-container">
      <form
        className="form"
        onSubmit={handleSubmit((submission) => {
          onSubmit(submission as any);
        })}
      >
        <div className="form-section">
          {/* Farmer */}
          <div className="form-row">
            <label>Farmer</label>
            <Controller
              name="farmerId"
              control={control}
              render={({fieldState: {error}}) => (
                <FormControl component="fieldset" error={!!error} fullWidth>
                  {/* Our new custom search bar if no farmer is selected */}
                  {!selectedFarmer && (
                    <NewFarmerSearchBar
                      onSearchTermChange={setFarmerSearchTerm}
                      isLoading={isLoadingFarmers}
                    />
                  )}

                  {/* If a farmer is selected, or we have results to show */}
                  {(!!selectedFarmer ||
                    (!!farmerSearchTerm.length && !isLoadingFarmers)) && (
                    <FarmerList
                      farmers={selectedFarmer ? [selectedFarmer] : farmers}
                      isSelected={!!selectedFarmer}
                      onFarmerSelect={handleFarmerSelect}
                      onFarmerDeselect={handleFarmerDeselect}
                    />
                  )}
                  <FormHelperText>{error?.message || null}</FormHelperText>
                </FormControl>
              )}
              rules={{
                required: 'Farmer required',
              }}
            />
          </div>

          {/* Field => store as "fieldPolygon" */}
          <div className="form-row">
            <label>Field</label>
            <Controller
              name="fieldPolygon"
              control={control}
              render={({field: {onChange, value}, fieldState: {error}}) => {
                const fieldOptions = selectedFarmer?.farm?.fields || [];
                console.log('Field Autocomplete render:', {
                  selectedFarmer: !!selectedFarmer,
                  hasFarm: !!selectedFarmer?.farm,
                  hasFields: !!selectedFarmer?.farm?.fields,
                  fieldOptions,
                  fieldOptionsCount: fieldOptions.length,
                  value,
                });

                return (
                  <Autocomplete
                    id="fieldPolygon"
                    options={fieldOptions}
                    getOptionLabel={(polygon) =>
                      `${polygon?.info?.fieldName} - ${polygon?.info?.hectares}ha`
                    }
                    value={(value as any) || null}
                    disabled={props.readonly || isLoading}
                    size="small"
                    onChange={(event, newValue) => {
                      onChange(newValue);
                      if (newValue) {
                        const {produce, hectares} = newValue.info as any;
                        if (produce) {
                          setValue('produceId', String(produce.id), {
                            shouldDirty: true,
                            shouldTouch: true,
                            shouldValidate: true,
                          });
                        }
                        if (hectares) {
                          setValue('hectares', hectares, {
                            shouldDirty: true,
                            shouldTouch: true,
                            shouldValidate: true,
                          });
                        }
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        className="input"
                        label="Select Field (optional)"
                        variant="outlined"
                        error={!!error}
                        helperText={error ? error.message : null}
                        disabled={props.readonly || isLoading}
                        size="medium"
                      />
                    )}
                  />
                );
              }}
              // rules={{
              //   required: 'Field required',
              // }}
            />
          </div>

          {/* Crop */}
          <div className="form-row">
            <label>Crop</label>
            {props.isLoadingProduce && (
              <Skeleton variant="rect" height={54} className="input" />
            )}
            {!props.isLoadingProduce && (
              <Controller
                name="produceId"
                control={control}
                render={({field: {onChange, value}, fieldState: {error}}) => (
                  <Autocomplete
                    id="produce"
                    options={props.allProduce || []}
                    disabled={
                      props.readonly ||
                      props.isLoadingProduce ||
                      props.isLoading
                    }
                    getOptionLabel={(p: IProduce) => p.name}
                    size="small"
                    value={findProduce(value) || null}
                    onChange={(event, produce) => {
                      onChange(produce?.id);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        className="input"
                        label="Search crop"
                        variant="outlined"
                        error={!!error}
                        helperText={error ? error.message : null}
                        disabled={
                          props.readonly ||
                          props.isLoadingProduce ||
                          props.isLoading
                        }
                        size="medium"
                      />
                    )}
                  />
                )}
                rules={{
                  required: 'Crop required',
                }}
              />
            )}
          </div>

          {/* Hectares */}
          <div className="form-row">
            <label>Hectares</label>
            <Controller
              name="hectares"
              control={control}
              render={({field: {onChange, value}, fieldState: {error}}) => (
                <TextField
                  className="input"
                  label="Select hectares"
                  variant="outlined"
                  type="number"
                  value={value}
                  onChange={onChange}
                  error={!!error}
                  helperText={error ? error.message : null}
                  disabled={props.readonly || props.isLoading}
                />
              )}
              rules={{
                required: 'Hectares required',
              }}
            />
          </div>

          {/* Type */}
          <div className="form-row">
            <label>Type</label>
            <Controller
              name="type"
              control={control}
              render={({field: {onChange, value}, fieldState: {error}}) => (
                <Autocomplete
                  id="type"
                  options={props.programTypes || []}
                  disabled={
                    props.readonly ||
                    props.isLoadingProgramTypes ||
                    props.isLoading
                  }
                  size="small"
                  value={value || null}
                  onChange={(event, newType) => {
                    onChange(newType);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      className="input"
                      label="Select Type"
                      variant="outlined"
                      error={!!error}
                      helperText={error ? error.message : null}
                      disabled={
                        props.readonly ||
                        props.isLoadingProgramTypes ||
                        props.isLoading
                      }
                      size="medium"
                    />
                  )}
                />
              )}
              rules={{
                required: 'Type required',
              }}
            />
          </div>

          {/* Soil Data Section */}
          <div className="form-row">
            <Box
              onClick={() => setShowSoilData(!showSoilData)}
              style={{
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '8px',
              }}
            >
              <label style={{margin: 0}}>Add soil data</label>
              <Button
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowSoilData(!showSoilData);
                }}
              >
                {showSoilData ? 'Hide' : 'Show'}
              </Button>
            </Box>
            <Collapse in={showSoilData}>
              <Box style={{marginTop: '0'}}>
                {/* Soil Type Selection */}
                <Box style={{marginBottom: '16px'}}>
                  <Typography variant="subtitle2" style={{marginBottom: '8px'}}>
                    Soil type
                  </Typography>
                  <Box style={{display: 'flex', flexWrap: 'wrap', gap: '8px'}}>
                    {SOIL_TYPES.map((type) => (
                      <Controller
                        key={type}
                        name="soilData.soilType"
                        control={control}
                        render={({field: {onChange, value}}) => (
                          <Chip
                            label={type}
                            clickable
                            onClick={() => onChange(type)}
                            style={{
                              backgroundColor:
                                value === type ? '#4caf50' : '#f5f5f5',
                              color: value === type ? '#fff' : '#333',
                            }}
                          />
                        )}
                      />
                    ))}
                  </Box>
                </Box>

                {/* Primary Nutrients */}
                <Box style={{marginTop: '16px', marginBottom: '16px'}}>
                  <Typography
                    variant="subtitle2"
                    style={{marginBottom: '12px', fontWeight: 500}}
                  >
                    Primary nutrients
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Controller
                        name="soilData.P"
                        control={control}
                        render={({
                          field: {onChange, value},
                          fieldState: {error},
                        }) => (
                          <TextField
                            className="input"
                            label="Phosphorus levels (P)"
                            variant="outlined"
                            type="number"
                            value={value || ''}
                            onChange={(e) =>
                              onChange(
                                e.target.value
                                  ? parseFloat(e.target.value)
                                  : undefined,
                              )
                            }
                            error={!!error}
                            helperText={error?.message}
                            disabled={props.readonly || props.isLoading}
                            fullWidth
                            size="small"
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Controller
                        name="soilData.K"
                        control={control}
                        render={({
                          field: {onChange, value},
                          fieldState: {error},
                        }) => (
                          <TextField
                            className="input"
                            label="Potassium levels (K)"
                            variant="outlined"
                            type="number"
                            value={value || ''}
                            onChange={(e) =>
                              onChange(
                                e.target.value
                                  ? parseFloat(e.target.value)
                                  : undefined,
                              )
                            }
                            error={!!error}
                            helperText={error?.message}
                            disabled={props.readonly || props.isLoading}
                            fullWidth
                            size="small"
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Controller
                        name="soilData.Ca"
                        control={control}
                        render={({
                          field: {onChange, value},
                          fieldState: {error},
                        }) => (
                          <TextField
                            className="input"
                            label="Calcium levels (Ca)"
                            variant="outlined"
                            type="number"
                            value={value || ''}
                            onChange={(e) =>
                              onChange(
                                e.target.value
                                  ? parseFloat(e.target.value)
                                  : undefined,
                              )
                            }
                            error={!!error}
                            helperText={error?.message}
                            disabled={props.readonly || props.isLoading}
                            fullWidth
                            size="small"
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Controller
                        name="soilData.Na"
                        control={control}
                        render={({
                          field: {onChange, value},
                          fieldState: {error},
                        }) => (
                          <TextField
                            className="input"
                            label="Sodium levels (Na)"
                            variant="outlined"
                            type="number"
                            value={value || ''}
                            onChange={(e) =>
                              onChange(
                                e.target.value
                                  ? parseFloat(e.target.value)
                                  : undefined,
                              )
                            }
                            error={!!error}
                            helperText={error?.message}
                            disabled={props.readonly || props.isLoading}
                            fullWidth
                            size="small"
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Controller
                        name="soilData.Mg"
                        control={control}
                        render={({
                          field: {onChange, value},
                          fieldState: {error},
                        }) => (
                          <TextField
                            className="input"
                            label="Magnesium levels (Mg)"
                            variant="outlined"
                            type="number"
                            value={value || ''}
                            onChange={(e) =>
                              onChange(
                                e.target.value
                                  ? parseFloat(e.target.value)
                                  : undefined,
                              )
                            }
                            error={!!error}
                            helperText={error?.message}
                            disabled={props.readonly || props.isLoading}
                            fullWidth
                            size="small"
                          />
                        )}
                      />
                    </Grid>
                  </Grid>
                </Box>

                {/* pH and Other Fields */}
                <Box style={{marginTop: '16px'}}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Controller
                        name="soilData.pH"
                        control={control}
                        render={({
                          field: {onChange, value},
                          fieldState: {error},
                        }) => (
                          <TextField
                            className="input"
                            label="pH levels"
                            variant="outlined"
                            type="number"
                            value={value || ''}
                            onChange={(e) =>
                              onChange(
                                e.target.value
                                  ? parseFloat(e.target.value)
                                  : undefined,
                              )
                            }
                            error={!!error}
                            helperText={error?.message}
                            disabled={props.readonly || props.isLoading}
                            fullWidth
                            size="small"
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Controller
                        name="soilData.cations"
                        control={control}
                        render={({
                          field: {onChange, value},
                          fieldState: {error},
                        }) => (
                          <TextField
                            className="input"
                            label="Total Cations (CEC)"
                            variant="outlined"
                            type="number"
                            value={value || ''}
                            onChange={(e) =>
                              onChange(
                                e.target.value
                                  ? parseFloat(e.target.value)
                                  : undefined,
                              )
                            }
                            error={!!error}
                            helperText={error?.message}
                            disabled={props.readonly || props.isLoading}
                            fullWidth
                            size="small"
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Controller
                        name="soilData.exchangeableAcidity"
                        control={control}
                        render={({
                          field: {onChange, value},
                          fieldState: {error},
                        }) => (
                          <TextField
                            className="input"
                            label="Exchangeable Acidity"
                            variant="outlined"
                            type="number"
                            value={value || ''}
                            onChange={(e) =>
                              onChange(
                                e.target.value
                                  ? parseFloat(e.target.value)
                                  : undefined,
                              )
                            }
                            error={!!error}
                            helperText={error?.message}
                            disabled={props.readonly || props.isLoading}
                            fullWidth
                            size="small"
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Controller
                        name="soilData.anions"
                        control={control}
                        render={({
                          field: {onChange, value},
                          fieldState: {error},
                        }) => (
                          <TextField
                            className="input"
                            label="Anions (optional)"
                            variant="outlined"
                            type="number"
                            value={value || ''}
                            onChange={(e) =>
                              onChange(
                                e.target.value
                                  ? parseFloat(e.target.value)
                                  : undefined,
                              )
                            }
                            error={!!error}
                            helperText={error?.message}
                            disabled={props.readonly || props.isLoading}
                            fullWidth
                            size="small"
                          />
                        )}
                      />
                    </Grid>
                  </Grid>
                </Box>
              </Box>
            </Collapse>
          </div>
        </div>

        {/* Sticky Buttons */}
        <div className="sticky-buttons">
          <div className="left-button">
            <Button
              variant="contained"
              onClick={props.onCancel}
              className="cancel-button"
            >
              {props.cancelButtonTitle || 'Clear All'}
            </Button>
          </div>

          <div className="right-button">
            <Button
              variant="contained"
              onClick={props.handleAIClick}
              className="ai-auto-create-btn"
              startIcon={<i className="fas fa-sparkles" />}
            >
              <FontAwesomeIcon icon={faSparkles} className="ai-icon" />
              AI Auto Create
            </Button>

            <Button
              variant="contained"
              color="primary"
              type="submit"
              disabled={
                props.isLoading ||
                props.isLoadingProduce ||
                props.isLoadingProgramTypes
              }
              className="create-btn"
            >
              {props.isLoading ? (
                <CircularProgress color="primary" size={24} thickness={6} />
              ) : (
                props.submitButtonTitle
              )}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}
